import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { environment } from '../../../environments/environment';

export interface MovementDetail {
  qty: number;
  items: any[];
}

export interface StockMovementRecord {
  date: string;
  openingStock: number;
  inward: {
    grn: MovementDetail;
    transfer: MovementDetail;
    repackInput: MovementDetail;
    salesReturn: MovementDetail;
    // Stock-take corrections (mobile session or web draft upload) that
    // INCREASED stock.
    stockTake: MovementDetail;
    // Manual/other stock adjustments with a positive quantity. Without a
    // column for these the closing balance moved with no visible cause and
    // the row failed to reconcile.
    other: MovementDetail;
  };
  outward: {
    transfer: MovementDetail;
    billing: MovementDetail;
    prn: MovementDetail;
    writeOff: MovementDetail;
    repackOutput: MovementDetail;
    // Stock-take corrections that DECREASED stock.
    stockTake: MovementDetail;
    // Manual/other stock adjustments with a negative quantity.
    other: MovementDetail;
  };
  closingStock: number;
}

export interface StockMovementMeta {
  productName: string;
  sku: string;
  location: string;
  totalAvailableStock: number;
  fromDate: string;
  toDate: string;
}

export interface StockMovementResponse {
  meta: StockMovementMeta;
  data: StockMovementRecord[];
  pagination: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

@Injectable({
  providedIn: 'root',
})
export class StockMovementService {
  private baseUrl = environment.apiUrl;

  constructor(private http: HttpClient) {}

  /**
   * Fetch stock movement register data.
   */
  getStockMovement(
    variantId: string,
    storeIds: string | string[],
    fromDate: string,
    toDate: string,
    page: number,
    limit: number
  ): Observable<StockMovementResponse> {
    let params = new HttpParams()
      .set('variantId', variantId)
      .set('fromDate', fromDate)
      .set('toDate', toDate)
      .set('page', page)
      .set('limit', limit);

    if (Array.isArray(storeIds)) {
      storeIds.forEach((id) => {
        params = params.append('storeIds', id);
      });
    } else {
      params = params.append('storeIds', storeIds); // Changed to append for consistency, though set is also fine
    }

    return this.http
      .get<StockMovementResponse>(`${this.baseUrl}/stock-movement/register`, {
        params,
      })
      .pipe(
        catchError((error) => {
          if (error.status === 404) {
            // Return empty structure on 404
            return of({
              meta: {
                productName: '',
                sku: '',
                location: '',
                totalAvailableStock: 0,
                fromDate,
                toDate,
              },
              data: [],
              pagination: {
                total: 0,
                page,
                limit,
                totalPages: 0,
              },
            } as StockMovementResponse);
          }
          throw error;
        })
      );
  }
}
